import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
    FilterSetController,
    Filter,
    Card,
    Status,
    EmptyState,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Icon,
    TableHeaderCell,
    CheckboxLabel,
    DropdownItem,
    Tooltip,
    Paginator,
    Loader
} from '@weezevent/nacre';
import SortDropdown from '../../components/sortDropdown';
import { useTranslation } from 'react-i18next';
import Style from '../attendee/list/list.module.css';
import { InfoTextToolTipMemo } from '../attendee/list/components/attendee-row/attendee-row';
import './listeEvent.scss';
import useAppContext from '../../context/useAppContext';
import SubscriptionLink from '../../models/subscriptionLink';

function EventsLoading({ isLoadingEvents = false }) {
    if (!isLoadingEvents) {
        return null;
    }
    return <Loader inline="centered" className="loader" />;
}
EventsLoading.propTypes = {
    isLoadingEvents: PropTypes.bool
};

export default function ListEvent({ setState, subscriptionId, organizationId }) {
    const { t } = useTranslation();
    const history = useHistory();

    const [filter, setFilter] = React.useState({ ordering: '', checkboxLinked: true, checkboxUnLinked: true, search: undefined, status: new Set(['valid']) });
    const [{ organization }] = useAppContext();
    const label = [
        { label: t('ticket.webservice.arrayLabel.distributorEvent'), name: 'name', align: 'left' },
        { label: t('ticket.webservice.arrayLabel.weezEvent'), name: 'event_name', align: 'left' },
        { label: t('ticket.webservice.arrayLabel.lastSync'), name: 'last_position', align: 'left' },
        { label: t('ticket.webservice.arrayLabel.nextSync'), name: 'next_update', align: 'left' }
        // { label: 'Statut', name: 'publication_status', align: 'left' }
    ];

    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 100, offset: 0 }));
    const [queryParams, setQueryParams] = React.useState({ search: '', status__in: 'valid', ...defaultPagination });
    const [subscriptionLinks, loadingLinks] = SubscriptionLink.useApiModel(
        {
            cache: false,
            allow_cache: false,
            organizationId,
            subscriptionId,
            query: queryParams
        },
        [subscriptionId, organizationId, queryParams]
    );

    const subscriptionLinksFiltered = React.useMemo(() => {
        if (loadingLinks || !subscriptionLinks) {
            return [];
        }

        let ret = subscriptionLinks;
        const _filter = filter;
        if (!_filter.checkboxLinked && _filter.checkboxUnLinked) {
            ret = ret.filter(e => e.event_id);
        }
        if (_filter.checkboxLinked && !_filter.checkboxUnLinked) {
            ret = ret.filter(e => !e.event_id);
        }

        if (ret && _filter.ordering && _filter.ordering.length > 0) {
            if (_filter.ordering === 'name')
                ret = ret.sort((a, b) => {
                    let fa = a.name.toLowerCase(),
                        fb = b.name.toLowerCase();
                    if (fa < fb) {
                        return -1;
                    }
                    if (fa > fb) {
                        return 1;
                    }
                    return 0;
                });
            if (_filter.ordering === '-name')
                ret = ret.sort((b, a) => {
                    let fa = a.name.toLowerCase(),
                        fb = b.name.toLowerCase();
                    if (fa < fb) {
                        return -1;
                    }
                    if (fa > fb) {
                        return 1;
                    }
                    return 0;
                });
            if (_filter.ordering === 'event_name')
                ret = ret.sort((a, b) => {
                    if (a.event_name && b.event_name) {
                        let fa = a.event_name.toLowerCase(),
                            fb = b.event_name.toLowerCase();
                        if (fa < fb) {
                            return -1;
                        }
                        if (fa > fb) {
                            return 1;
                        }
                    }
                    if (a.event_name) {
                        return 1;
                    }
                    if (b.event_name) {
                        return -1;
                    }
                    return 0;
                });
            if (_filter.ordering === '-event_name')
                ret = ret.sort((b, a) => {
                    if (a.event_name && b.event_name) {
                        let fa = a.event_name.toLowerCase(),
                            fb = b.event_name.toLowerCase();
                        if (fa < fb) {
                            return -1;
                        }
                        if (fa > fb) {
                            return 1;
                        }
                    }
                    if (a.event_name) {
                        return 1;
                    }
                    if (b.event_name) {
                        return -1;
                    }
                    return 0;
                });

            if (_filter.ordering === 'last_position')
                ret = ret.sort((a, b) => {
                    let da = new Date(a.last_position),
                        db = new Date(b.last_position);
                    return da - db;
                });
            if (_filter.ordering === '-last_position')
                ret = ret.sort((b, a) => {
                    let da = new Date(a.last_position),
                        db = new Date(b.last_position);
                    return da - db;
                });
            if (_filter.ordering === 'next_update')
                ret = ret.sort((a, b) => {
                    let da = new Date(a.last_position),
                        db = new Date(b.last_position);
                    return da - db;
                });
            if (_filter.ordering === '-next_update')
                ret = ret.sort((b, a) => {
                    let da = new Date(a.next_update),
                        db = new Date(b.next_update);
                    return da - db;
                });
        }

        return ret.length ? ret : [];
    }, [filter, subscriptionLinks, loadingLinks]);

    const options = [
        { key: 'valid', text: t('ticket.webservice.optionLabel.actifOption'), name: 'valid' },
        { key: 'trouble', text: t('ticket.webservice.optionLabel.inactifOption'), name: 'trouble' },
        { key: 'hidden', text: t('ticket.webservice.optionLabel.finishOption'), name: 'hidden' }
    ];

    let onChangeFilter = _filter => {
        if (!_filter.search) {
            _filter.search = '';
        }
        if (!_filter.status) {
            _filter.status = '';
        }

        let paramChange = new Set();

        const statusString = Array.from(_filter.status).join(',');
        if (statusString !== queryParams['status__in']) {
            paramChange = { ...paramChange, status__in: Array.from(_filter.status).join(',') };
        }

        if (_filter.search !== queryParams['search']) {
            paramChange = { ...paramChange, search: _filter.search };
        }
        if (paramChange.size !== 0) {
            setQueryParams({ ...queryParams, ...paramChange });
        }
    };

    const totalCount = React.useMemo(() => {
        return Number.parseInt(subscriptionLinks?.__meta?.headers.get('total-count')) || 0;
    }, [subscriptionLinks]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);
            setQueryParams({ ...queryParams, offset: queryParams.limit * value });
        },
        [queryParams]
    );

    return (
        <>
            <Card className="cardArrayEvent">
                <Card.Content>
                    <FilterSetController
                        filterLabel={t('ticket.rates.filters.label_filters')}
                        label={t('ticket.common.action.search')}
                        search={{
                            onChange: (synth, value) => {
                                let _filter = { ...filter, search: value };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            handleReset: (synth, value) => {
                                let _filter = { ...filter, search: '' };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            },
                            displayResetIcon: true,
                            placeholder: t('ticket.webservice.searchEvents'),
                            value: filter?.search || ''
                        }}
                        onChange={values => {
                            let _filter = { ...filter, status: values.status };
                            setFilter(_filter);
                            onChangeFilter(_filter);
                        }}
                        reset={() => {
                            //setFilters({});
                        }}
                        initialValues={{ status: new Set(['valid']) }}
                        activeReset
                        resetLabel={{ content: t('ticket.common.action.reset') }}
                        total={totalCount}
                        isLoadingTotal={loadingLinks}
                        labelTotal={t('ticket.webservice.events')}
                    >
                        <Filter
                            name="status"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            multiple
                            options={options}
                            index="1"
                        />
                    </FilterSetController>
                    <div className="contentCheckbox">
                        <div>Afficher:</div>

                        <CheckboxLabel
                            name="checkboxA"
                            label={t('ticket.webservice.list.checkboxlink')}
                            checked
                            onChange={() => {
                                let _filter = { ...filter, checkboxUnLinked: !filter.checkboxUnLinked };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            }}
                            onClick={() => {}}
                        />
                        <CheckboxLabel
                            name="checkboxA"
                            label={t('ticket.webservice.list.checkboxUnlink')}
                            checked
                            onChange={() => {
                                let _filter = { ...filter, checkboxLinked: !filter.checkboxLinked };
                                setFilter(_filter);
                                onChangeFilter(_filter);
                            }}
                            onClick={() => {}}
                        />
                    </div>
                    <div className="dropdown"></div>
                </Card.Content>
                <Table className={(Style['list__table'], 'webServicesTable')}>
                    <Table.THead>
                        <Table.Tr isHeader>
                            {label.map((obj, key) => (
                                <SortDropdown
                                    className={obj.align === 'center' ? 'center' : ''}
                                    name={obj.name}
                                    key={key}
                                    label={obj.label}
                                    ordering={filter.ordering}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={t('ticket.accounting.filter.increasing')}
                                            onClick={() => {
                                                let _filter = { ...filter, ordering: obj.name };
                                                setFilter(_filter);
                                                onChangeFilter(_filter);
                                            }}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('ticket.accounting.filter.descending')}
                                            onClick={() => {
                                                let _filter = { ...filter, ordering: '-' + obj.name };
                                                setFilter(_filter);
                                                onChangeFilter(_filter);
                                            }}
                                        />
                                    ]}
                                />
                            ))}

                            <TableHeaderCell className={'center'} style={{ width: '100px' }}>
                                {t('ticket.webservice.list.attendeesArrayHeader')}
                            </TableHeaderCell>

                            <TableHeaderCell className={'center'} style={{ width: '100px' }}>
                                {t('ticket.webservice.list.statusArrayHeader')}
                            </TableHeaderCell>

                            <TableHeaderCell className={'center'} style={{ width: '100px' }}>
                                {t('ticket.accounting.filter.actionLabel')}
                            </TableHeaderCell>
                        </Table.Tr>
                    </Table.THead>
                    {subscriptionLinksFiltered.length > 0 && (
                        <TableBody>
                            {subscriptionLinksFiltered.map((value, key) => (
                                <TableRow
                                    key={key}
                                    withHoverHighlightRow={true}
                                    onClick={() => {
                                        history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/attendees?subscription_link_id__in=${value.id}`);
                                    }}
                                >
                                    <TableCell>
                                        <InfoTextToolTipMemo tooltipCopy={Boolean(value.name)} title={value.name ? value.name : '-'} />
                                        <InfoTextToolTipMemo
                                            tooltipCopy={Boolean(value.id)}
                                            className="secondTips"
                                            title={
                                                (value.id ? 'N° ID · ' + value.id : '-') +
                                                ' · ' +
                                                (value.raw_data?.raw?.date_start
                                                    ? new Date(value.raw_data.raw.date_start).toLocaleDateString([], { timeZone: organization.timezone }) +
                                                      ' ' +
                                                      new Date(value.raw_data.raw.date_start).toLocaleTimeString([], { timeZone: organization.timezone }) +
                                                      ' ' +
                                                      (new Date(value.raw_data.raw.date_start).getTimezoneOffset() ?? '')
                                                    : '-')
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo tooltipCopy={Boolean(value.event_name)} title={value.event_name ? value.event_name : '-'} />
                                        <InfoTextToolTipMemo tooltipCopy={Boolean(value.event_id)} className="secondTips" title={value.event_id ? 'E' + value.event_id : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.last_position ? new Date(value.last_position).toLocaleDateString() : '-'} />
                                        <InfoTextToolTipMemo className="secondTips" title={value.last_position ? new Date(value.last_position).toLocaleTimeString() : '-'} />
                                    </TableCell>
                                    <TableCell>
                                        <InfoTextToolTipMemo title={value.next_update ? new Date(value.next_update).toLocaleDateString() : '-'} />
                                        <InfoTextToolTipMemo className="secondTips" title={value.next_update ? new Date(value.next_update).toLocaleTimeString() : '-'} />
                                    </TableCell>
                                    <TableCell>{value.tickets_count}</TableCell>
                                    <TableCell>
                                        <div>
                                            {value.lock !== null ? (
                                                <Tooltip
                                                    title={t('ticket.webservice.list.status-processing-tooltip', {
                                                        time: Math.floor((Date.now() - Date.parse(value.lock)) / (1000 * 60))
                                                    })}
                                                    triggerOnHover
                                                >
                                                    <Status text={t('ticket.webservice.list.statusList.processing')} color={'orange'} responsive />
                                                </Tooltip>
                                            ) : value.status === 'hidden' ? (
                                                <Status text={t('ticket.webservice.list.statusList.finish')} color={''} responsive />
                                            ) : value.status === 'valid' ? (
                                                <Status text={t('ticket.webservice.list.statusList.actif')} color={'green'} responsive />
                                            ) : (
                                                <Status text={t('ticket.webservice.list.statusList.inactif')} color={'red'} responsive />
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div>
                                            {!value.event_id ? (
                                                <Tooltip title={t('ticket.webservice.list.link')} triggerOnHover={true} position={'top'}>
                                                    <Icon
                                                        onClick={e => {
                                                            setState({
                                                                currentSubscriptionLink: value
                                                            });
                                                        }}
                                                        name="link-bis"
                                                        size="Regular"
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={t('ticket.webservice.list.unLink')} triggerOnHover={true} position={'top'}>
                                                    <Icon
                                                        onClick={e => {
                                                            setState({ currentRemoveLink: value });
                                                        }}
                                                        name="unlink"
                                                        size="Regular"
                                                    />
                                                </Tooltip>
                                            )}
                                            {!value.event_id ? null : (
                                                <Tooltip title={t('ticket.webservice.list.resetLink')} triggerOnHover={true} position={'top'}>
                                                    <Icon
                                                        onClick={e => {
                                                            setState({ currentResetLink: value });
                                                        }}
                                                        name="Reset"
                                                        size="Regular"
                                                    />
                                                </Tooltip>
                                            )}
                                            {!value.lock ? null : (
                                                <Tooltip title={t('ticket.webservice.list.unlockLink')} triggerOnHover={true} position={'top'}>
                                                    <Icon
                                                        onClick={e => {
                                                            setState({ currentUnlockLink: value });
                                                        }}
                                                        name="rocket"
                                                        size="Regular"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
                {(subscriptionLinksFiltered.length === 0 || subscriptionLinks === undefined || subscriptionLinks.length === 0) && (
                    <EmptyState icon={<Icon name="list" />} title={t('ticket.webservice.list.noEvent')} />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <Paginator currentPage={currentPage} onPageChange={handlePage} pageSize={queryParams.limit} totalCount={totalCount} />
                </div>
            </Card>
        </>
    );
}

ListEvent.propTypes = {
    subscriptionId: PropTypes.object.isRequired,
    organizationId: PropTypes.object.isRequired
};
